import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, LOGO_URL } from "@/lib/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#gallery" },
  { label: "How It Works", href: "#process" },
  { label: "Reviews", href: "#reviews" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-cream/95 backdrop-blur border-b border-line" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-3"
        >
          <img src={LOGO_URL} alt={BUSINESS.fullName} className="h-12 w-auto" />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm font-semibold text-ink-soft hover:text-forest transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-bold text-forest"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phone}
          </a>
          <button
            data-testid="nav-get-quote"
            onClick={() => navigate("/quote")}
            className="rounded-full bg-forest text-cream px-6 py-3 text-sm font-bold hover:bg-forest-dark transition-colors"
          >
            Get a Free Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-forest"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-cream border-b border-line px-5 pb-6 pt-2 space-y-2">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="block w-full text-left py-3 text-base font-semibold text-ink border-b border-line/60"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-get-quote-mobile"
            onClick={() => { setOpen(false); navigate("/quote"); }}
            className="mt-3 w-full rounded-full bg-forest text-cream px-6 py-3.5 text-base font-bold"
          >
            Get a Free Quote
          </button>
        </div>
      )}
    </header>
  );
};
